import { useEffect, useState, type FormEvent } from "react";
import { CheckCircle2, KeyRound, Shield, ShieldCheck, ShieldOff } from "lucide-react";
import { api, ApiError } from "../api";
import { Button, Card, Field } from "../ui";
import { PasswordInput } from "./PasswordInput";

type MfaStatus = {
  enabled: boolean;
  pending: boolean;
  confirmedAt?: string | null;
  lastUsedAt?: string | null;
  backupCodesRemaining?: number;
  /**
   * False when the server has no MFA_ENCRYPTION_KEY configured. We
   * disable enrollment in that case rather than silently writing
   * plaintext secrets, and the UI explains why.
   */
  configured?: boolean;
};

type StartResponse = {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
};

type ConfirmResponse = { ok: true; backupCodes: string[] };

/**
 * Two-factor auth card on the Settings page.
 *
 * Three sub-views, driven by `phase`:
 *   - "idle"     : status pill + Enable button (or Disable when on)
 *   - "setup"    : QR code + secret + first-code prompt (during enroll)
 *   - "codes"    : one-shot backup-code list shown after success
 *                  (and after regenerate)
 *
 * Disable opens an inline confirm form (current password + a TOTP
 * code), to prevent a stolen session from quietly turning MFA off.
 */
export function MfaCard() {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<"idle" | "setup" | "codes">("idle");
  const [setup, setSetup] = useState<StartResponse | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const [showDisable, setShowDisable] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get<MfaStatus>("/api/me/mfa");
      setStatus(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const startEnroll = async () => {
    setError(null);
    setBusy(true);
    try {
      const r = await api.post<StartResponse>("/api/me/mfa/start");
      setSetup(r);
      setSetupCode("");
      setPhase("setup");
    } catch (err) {
      setError(extractError(err) ?? "Couldn't start MFA setup.");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await api.post<ConfirmResponse>("/api/me/mfa/confirm", {
        code: setupCode.trim(),
      });
      setBackupCodes(r.backupCodes);
      setPhase("codes");
      await load();
    } catch (err) {
      setError(
        extractError(err) === "code_invalid"
          ? "That code doesn't match. Make sure the time on your phone is correct, then try the next one your authenticator shows."
          : "Couldn't verify the code.",
      );
    } finally {
      setBusy(false);
    }
  };

  const cancelEnroll = () => {
    setPhase("idle");
    setSetup(null);
    setSetupCode("");
    setError(null);
  };

  const submitDisable = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/api/me/mfa/disable", {
        currentPassword: disablePwd,
        code: disableCode.trim() || undefined,
      });
      setShowDisable(false);
      setDisablePwd("");
      setDisableCode("");
      setBackupCodes([]);
      setPhase("idle");
      await load();
    } catch (err) {
      const code = extractError(err);
      setError(
        code === "current_password_invalid"
          ? "Current password is incorrect."
          : code === "code_invalid"
            ? "That authenticator code didn't match."
            : code === "code_required"
              ? "Enter a current authenticator code to confirm."
              : "Couldn't disable MFA.",
      );
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    const code = window.prompt(
      "Enter a current 6-digit code from your authenticator to regenerate backup codes.",
    );
    if (!code) return;
    setError(null);
    setBusy(true);
    try {
      const r = await api.post<ConfirmResponse>(
        "/api/me/mfa/regenerate-backup-codes",
        { code: code.trim() },
      );
      setBackupCodes(r.backupCodes);
      setPhase("codes");
      await load();
    } catch (err) {
      setError(
        extractError(err) === "code_invalid"
          ? "That code didn't match. Try the next code your authenticator shows."
          : "Couldn't regenerate backup codes.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
        <Shield size={18} /> Two-factor authentication
      </h2>
      <p className="text-xs text-ink-500 mb-3">
        Add a one-time code from an authenticator app (Google Authenticator, 1Password,
        Authy, …) to your sign-in. Optional — but strongly recommended for owners on
        the Board or with admin access.
      </p>

      {loading ? (
        <p className="text-ink-500 text-sm">Loading…</p>
      ) : phase === "setup" && setup ? (
        <SetupPanel
          setup={setup}
          code={setupCode}
          onCodeChange={setSetupCode}
          onConfirm={confirmEnroll}
          onCancel={cancelEnroll}
          busy={busy}
          error={error}
        />
      ) : phase === "codes" ? (
        <BackupCodesPanel
          codes={backupCodes}
          onDone={() => {
            setPhase("idle");
            setBackupCodes([]);
          }}
        />
      ) : (
        <IdleView
          status={status}
          busy={busy}
          error={error}
          onEnroll={startEnroll}
          showDisable={showDisable}
          onShowDisable={() => setShowDisable(true)}
          onCancelDisable={() => {
            setShowDisable(false);
            setDisablePwd("");
            setDisableCode("");
            setError(null);
          }}
          disablePwd={disablePwd}
          setDisablePwd={setDisablePwd}
          disableCode={disableCode}
          setDisableCode={setDisableCode}
          submitDisable={submitDisable}
          onRegenerate={regenerate}
        />
      )}
    </Card>
  );
}

function IdleView({
  status,
  busy,
  error,
  onEnroll,
  showDisable,
  onShowDisable,
  onCancelDisable,
  disablePwd,
  setDisablePwd,
  disableCode,
  setDisableCode,
  submitDisable,
  onRegenerate,
}: {
  status: MfaStatus | null;
  busy: boolean;
  error: string | null;
  onEnroll: () => void;
  showDisable: boolean;
  onShowDisable: () => void;
  onCancelDisable: () => void;
  disablePwd: string;
  setDisablePwd: (v: string) => void;
  disableCode: string;
  setDisableCode: (v: string) => void;
  submitDisable: (e: FormEvent) => void;
  onRegenerate: () => void;
}) {
  if (!status) return null;
  if (!status.enabled) {
    const notConfigured = status.configured === false;
    return (
      <div>
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-sand-100 text-ink-700 text-xs mb-3">
          <ShieldOff size={14} />
          MFA is off
        </div>
        {notConfigured && (
          <div className="alert-error mb-3">
            Two-factor authentication isn't enabled on this server. Ask your
            administrator to set <code>MFA_ENCRYPTION_KEY</code> in the
            environment.
          </div>
        )}
        {error && <div className="alert-error">{error}</div>}
        <div>
          <Button
            variant="primary"
            onClick={onEnroll}
            loading={busy}
            disabled={busy || notConfigured}
          >
            <KeyRound size={14} /> Set up two-factor
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-status-approved-bg text-status-approved-fg text-xs mb-3">
        <ShieldCheck size={14} />
        MFA is on
      </div>
      <p className="text-xs text-ink-500 mb-3">
        Backup codes remaining:{" "}
        <strong>{status.backupCodesRemaining ?? 0}</strong>
        {(status.backupCodesRemaining ?? 0) <= 2 && (
          <>
            {" "}
            <span className="text-status-rejected-fg">
              (running low — regenerate soon)
            </span>
          </>
        )}
      </p>
      {error && <div className="alert-error">{error}</div>}
      {!showDisable ? (
        <div className="flex gap-2 flex-wrap">
          <Button variant="secondary" onClick={onRegenerate} loading={busy}>
            Regenerate backup codes
          </Button>
          <Button variant="secondary" onClick={onShowDisable}>
            <ShieldOff size={14} /> Turn off
          </Button>
        </div>
      ) : (
        <form onSubmit={submitDisable} className="mt-2">
          <p className="text-xs text-ink-500 mb-2">
            Enter your current password and a 6-digit code from your
            authenticator to turn off two-factor.
          </p>
          <Field label="Current password">
            <PasswordInput
              value={disablePwd}
              onChange={setDisablePwd}
              autoComplete="current-password"
              required
            />
          </Field>
          <Field label="Authenticator code">
            <input
              className="input"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={disableCode}
              onChange={(e) =>
                setDisableCode(e.target.value.replace(/\D/g, ""))
              }
              required
            />
          </Field>
          <div className="flex gap-2">
            <Button variant="primary" type="submit" loading={busy}>
              Confirm and turn off
            </Button>
            <Button variant="secondary" type="button" onClick={onCancelDisable}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function SetupPanel({
  setup,
  code,
  onCodeChange,
  onConfirm,
  onCancel,
  busy,
  error,
}: {
  setup: StartResponse;
  code: string;
  onCodeChange: (v: string) => void;
  onConfirm: (e: FormEvent) => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <div>
      <ol className="text-sm text-ink-700 space-y-2 m-0 pl-5">
        <li>
          Scan the QR code with an authenticator app (Google Authenticator,
          1Password, Authy, …). You can paste the secret manually if you'd
          rather.
        </li>
        <li>Enter the 6-digit code your authenticator shows next.</li>
      </ol>

      <div className="mt-3 flex flex-col sm:flex-row gap-4 items-start">
        <img
          src={setup.qrDataUrl}
          alt="Two-factor authentication QR code"
          width={180}
          height={180}
          className="rounded-md border border-sand-200 bg-white p-2"
        />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-ink-500 mb-1">Secret (manual entry)</p>
          <code className="block text-[12.5px] break-all rounded bg-sand-100 px-2 py-1 text-ink-700">
            {setup.secret}
          </code>
          <p className="text-[11px] text-ink-400 mt-1">
            Issuer: The Elm at Clark Portal · 6 digits, 30s
          </p>
        </div>
      </div>

      <form onSubmit={onConfirm} className="mt-4">
        <Field label="Code from your authenticator">
          <input
            className="input"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, ""))}
            required
            autoFocus
          />
        </Field>
        {error && <div className="alert-error">{error}</div>}
        <div className="flex gap-2">
          <Button
            variant="primary"
            type="submit"
            loading={busy}
            disabled={busy || code.length !== 6}
          >
            Verify and enable
          </Button>
          <Button variant="secondary" type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function BackupCodesPanel({
  codes,
  onDone,
}: {
  codes: string[];
  onDone: () => void;
}) {
  const printable = codes.join("\n");
  return (
    <div>
      <div className="alert-success inline-flex items-center gap-2">
        <CheckCircle2 size={16} />
        Two-factor authentication is on.
      </div>
      <p className="text-sm text-ink-700 mt-3 mb-2">
        Save these one-time backup codes somewhere safe. Each one can be used
        once if you lose access to your authenticator. <strong>You won't see
        them again</strong> — regenerate from this page if you need a fresh
        set.
      </p>
      <pre className="rounded-md border border-sand-200 bg-sand-50 px-3 py-2 text-[13px] tracking-wide leading-7 m-0 whitespace-pre">
        {printable}
      </pre>
      <div className="flex gap-2 mt-3">
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(printable);
          }}
        >
          Copy to clipboard
        </Button>
        <Button variant="primary" onClick={onDone}>
          I've saved them
        </Button>
      </div>
    </div>
  );
}

function extractError(err: unknown): string | null {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string };
    return body?.error ?? null;
  }
  return null;
}
